import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import ReactTable from "react-table";

import { makeStyles } from "@material-ui/core/styles";
import History from "@material-ui/icons/History";
import Assignment from "@material-ui/icons/Assignment";

import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardIcon from "components/Card/CardIcon.js";
import CardHeader from "components/Card/CardHeader.js";
import Button from "components/CustomButtons/Button.js";
import CustomInput from "components/CustomInput/CustomInput.js";

import { cardTitle } from "assets/jss/material-dashboard-pro-react.js";

import { bookingApi } from "functions/apis";
import { token } from "functions/Helper";

const styles = {
  cardIconTitle: { ...cardTitle, marginTop: "15px", marginBottom: "0px" }
};
const useStyles = makeStyles(styles);

// Matches Sindo.Booking.Enums.AgentQuotaMutationType
const MUTATION_TYPE = {
  0: "Generated",
  10: "Admin Add",
  20: "Admin Release",
  30: "Booking Deduct",
  40: "Booking Release",
  50: "Transfer Deduct",
  60: "Transfer Release"
};

// Matches Sindo.Booking.Enums.AgentQuotaMutationStatus
const MUTATION_STATUS = {
  0: "Pending",
  10: "Allocated",
  20: "Error",
  30: "Enqueued"
};
// External-calling mutation types that can be retried when in Error.
const EXTERNAL_TYPES = [0, 10, 20];

export default function QuotaMutationList() {
  const source = useRef(new AbortController());
  const { id } = useParams();

  const canWrite =
    token["booking.agent_daily_quotas.write"] === "allowed" ||
    token.is_admin === "True";

  const [data, setData] = React.useState([]);
  const [pages, setPages] = React.useState(0);
  const [reload, setReload] = React.useState(0);
  const [quota, setQuota] = React.useState(null);
  const [pagination, setPagination] = React.useState({
    pageIndex: 1,
    pageSize: 10
  });
  const [loading, setLoading] = React.useState(false);

  useEffect(() => {
    return () => source.current.abort();
  }, []);

  useEffect(() => {
    if (!id) return;
    bookingApi
      .get("/AgentDailyQuotas/" + id, { signal: source.current.signal })
      .then(data => setQuota(data))
      .catch(error => console.log(error));
  }, [id, reload]);

  const retry = mutationID => {
    bookingApi
      .post("/AgentQuotaMutations/Retry/" + mutationID, null, {
        signal: source.current.signal
      })
      .then(() => setReload(r => r + 1))
      .catch(error => console.log(error));
  };

  useEffect(() => {
    setLoading(true);
    bookingApi
      .get("/AgentQuotaMutations", {
        signal: source.current.signal,
        params: {
          filter: { agentDailyQuotaID: id ?? null, sort: 1 },
          pagination
        }
      })
      .then(data => {
        setData(
          data.records.map(record => ({
            ...record,
            agentCode: record.agent?.code,
            routeCode: record.route?.code,
            dateLabel: record.date?.substring(0, 10),
            typeLabel: MUTATION_TYPE[record.mutationType],
            statusLabel: MUTATION_STATUS[record.status],
            createdAtLabel: record.createdAt
              ?.replace("T", " ")
              .substring(0, 19),
            actions:
              canWrite &&
              record.status === 20 &&
              EXTERNAL_TYPES.includes(record.mutationType) ? (
                <Button
                  color="warning"
                  size="sm"
                  onClick={() => retry(record.id)}
                >
                  Retry
                </Button>
              ) : null
          }))
        );
        setPages(Math.ceil(data.totalRecords / pagination.pageSize));
        setLoading(false);
      })
      .catch(error => console.log(error));
  }, [pagination, id, reload]);

  const classes = useStyles();
  return (
    <GridContainer>
      {id && quota !== null && (
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" icon>
              <CardIcon color="primary">
                <Assignment />
              </CardIcon>
              <h4 className={classes.cardIconTitle}>AGENT DAILY QUOTA</h4>
            </CardHeader>
            <CardBody>
              <GridContainer>
                <GridItem xs={12} md={6}>
                  <CustomInput
                    labelText="Agent"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      disabled: true,
                      value: quota.agent
                        ? quota.agent.code + " - " + quota.agent.name
                        : ""
                    }}
                  />
                </GridItem>
                <GridItem xs={12} md={6}>
                  <CustomInput
                    labelText="Route"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      disabled: true,
                      value: quota.route
                        ? quota.route.code + " - " + quota.route.name
                        : ""
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={4}>
                  <CustomInput
                    labelText="Date"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      disabled: true,
                      value: quota.date?.substring(0, 10) ?? ""
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={4}>
                  <CustomInput
                    labelText="Time"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      disabled: true,
                      value: quota.time ?? ""
                    }}
                  />
                </GridItem>
                <GridItem xs={12} sm={4}>
                  <CustomInput
                    labelText="Remaining"
                    formControlProps={{ fullWidth: true }}
                    inputProps={{
                      disabled: true,
                      value: quota.remaining
                    }}
                  />
                </GridItem>
              </GridContainer>
            </CardBody>
          </Card>
        </GridItem>
      )}
      <GridItem xs={12}>
        <Card>
          <CardHeader color="primary" icon>
            <CardIcon color="primary">
              <History />
            </CardIcon>
            <h4 className={classes.cardIconTitle}>QUOTA MUTATION HISTORY</h4>
          </CardHeader>
          <CardBody>
            <ReactTable
              manual
              pages={pages}
              page={pagination.pageIndex - 1}
              pageSize={pagination.pageSize}
              onPageChange={page =>
                setPagination({ ...pagination, pageIndex: page + 1 })
              }
              onPageSizeChange={(pageSize, page) =>
                setPagination({ pageSize, pageIndex: page + 1 })
              }
              loading={loading}
              data={data}
              filterable={false}
              columns={[
                { Header: "When", accessor: "createdAtLabel" },
                ...(id
                  ? []
                  : [
                      { Header: "Agent", accessor: "agentCode" },
                      { Header: "Route", accessor: "routeCode" },
                      { Header: "Date", accessor: "dateLabel" },
                      { Header: "Time", accessor: "time" }
                    ]),
                { Header: "Type", accessor: "typeLabel" },
                { Header: "Delta", accessor: "delta" },
                { Header: "Remaining", accessor: "remainingAfter" },
                { Header: "Status", accessor: "statusLabel" },
                { Header: "Ref ID", accessor: "coreApiReferenceID" },
                { Header: "Error", accessor: "error" },
                { Header: "Reference", accessor: "reference" },
                { Header: "Remarks", accessor: "remarks" },
                { Header: "By", accessor: "createdBy" },
                {
                  Header: "Actions",
                  accessor: "actions",
                  sortable: false,
                  filterable: false
                }
              ]}
              defaultPageSize={10}
              showPaginationTop
              showPaginationBottom={false}
              className="-striped -highlight"
            />
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
